/**
 * Preview Modal - Shows final product before publishing
 * 
 * Uses BookReadingView for the core reading experience (DRY).
 * NON-BLOCKING: Turn Art generates in background via Edge Function.
 * Uses Realtime subscriptions to update UI as illustrations complete.
 * 
 * Tabs:
 * - Preview: Reading view with Turn Art + Turn Color
 * - Turn Art: Edit inputs and regenerate illustrations
 * 
 * @see components/shared/BookReadingView.tsx for rendering
 * @see app/library/book/[id].tsx for parent reader (same BookReadingView)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, Loader2, AlertCircle, ArrowLeft, Send, Wand2, Eye, Palette
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { FEATURES } from '@/lib/feature-flags';
import { TurnColorMapping, STANDARD_COLORS, type TurnCodeError } from '@/lib/turn-color';
import { BookReadingView, type ReadingPage } from '@/components/shared';
import { TurnCodeFixModal } from '@/components/shared/TurnCodeFixModal';
import { linesToText, linesToPlainText, type DigitalPageBlock } from './types';
import {
  triggerTurnArtGeneration,
  getTurnArtProgressFromDb,
} from '@/services/scannedBookService';
import { TurnArtEditor } from './TurnArtEditor';
import { errorTracker } from '@/lib/logging';

// ============================================================================
// Types
// ============================================================================

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: () => Promise<void>;
  bookId: string;
  bookTitle: string;
  bookAuthor: string | null;
  blocks: DigitalPageBlock[];
}

type GenerationStatus = 
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

type TurnArtStatus = 'pending' | 'generating' | 'complete' | 'failed' | null;

type TabId = 'preview' | 'turn-art';

// ============================================================================
// Component
// ============================================================================

export function PreviewModal({
  isOpen,
  onClose,
  onPublish,
  bookId,
  bookTitle,
  bookAuthor,
  blocks,
}: PreviewModalProps) {
  // State
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turnArtStatus, setTurnArtStatus] = useState<TurnArtStatus>(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [previewPages, setPreviewPages] = useState<ReadingPage[]>([]);
  const [colorMappings, setColorMappings] = useState<TurnColorMapping[]>(STANDARD_COLORS);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('preview');
  const [artRefreshKey, setArtRefreshKey] = useState(0);
  
  // Turn Code error handling
  const [turnCodeError, setTurnCodeError] = useState<TurnCodeError | null>(null);
  const [showTurnCodeFix, setShowTurnCodeFix] = useState(false);
  const reportedErrorsRef = useRef<Set<string>>(new Set());
  
  // Refs for Realtime subscription
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const turnCodesRef = useRef<Map<string, string>>(new Map());

  // Load preview data when modal opens (NON-BLOCKING)
  useEffect(() => {
    if (isOpen && status === 'idle') {
      loadPreview();
    }
  }, [isOpen]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setPreviewPages([]);
      setCurrentPageIndex(0);
      setError(null);
      setTurnArtStatus(null);
      
      // Cleanup Realtime subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    }
  }, [isOpen]);

  // Subscribe to Turn Art updates via Realtime
  useEffect(() => {
    if (!isOpen || !bookId) return;
    
    // Get all source page IDs for this book's blocks
    const allSourceIds = blocks.flatMap(b => b.sourcePageIds);
    if (allSourceIds.length === 0) return;
    
    // Subscribe to scanned_book_pages updates
    const channel = supabase
      .channel(`turn-art-${bookId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'scanned_book_pages',
          filter: `book_id=eq.${bookId}`,
        },
        (payload) => {
          const newData = payload.new as { id: string; enhanced_image_url: string | null; page_number: number };
          
          if (newData.enhanced_image_url && allSourceIds.includes(newData.id)) {
            console.log(`[PreviewModal] Turn Art updated for page ${newData.page_number}`);
            
            // Update the specific page's Turn Art in state
            setPreviewPages(prev => prev.map((page, idx) => {
              const block = blocks[idx];
              if (block?.sourcePageIds[0] === newData.id) {
                return { ...page, turnArtUrl: newData.enhanced_image_url };
              }
              return page;
            }));
            
            // Update progress
            setProgress(prev => ({
              ...prev,
              completed: prev.completed + 1,
            }));
          }
        }
      )
      .subscribe();
    
    channelRef.current = channel;
    
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [isOpen, bookId, blocks]);

  // Refresh Turn Art URLs from database (defined before polling useEffect)
  const refreshTurnArtUrls = useCallback(async () => {
    const allSourceIds = blocks.flatMap(b => b.sourcePageIds);
    const { data: pagesWithArt } = await supabase
      .from('scanned_book_pages')
      .select('id, enhanced_image_url')
      .in('id', allSourceIds.length > 0 ? allSourceIds : ['__none__']);
    
    const artByPageId = new Map(
      (pagesWithArt || []).map(p => [p.id, p.enhanced_image_url])
    );
    
    setPreviewPages(prev => prev.map((page, idx) => {
      const block = blocks[idx];
      const turnArtUrl = block?.sourcePageIds[0] 
        ? artByPageId.get(block.sourcePageIds[0]) || null
        : null;
      return { ...page, turnArtUrl };
    }));
  }, [blocks]);

  // Poll for Turn Art status updates (fallback for Realtime)
  useEffect(() => {
    if (!isOpen || turnArtStatus === 'complete' || turnArtStatus === 'failed') return;
    
    const pollInterval = setInterval(async () => {
      const progressData = await getTurnArtProgressFromDb(bookId);
      setProgress({ completed: progressData.completed, total: progressData.total });
      setTurnArtStatus(progressData.status);
      
      // If complete, refresh the preview pages to get all Turn Art URLs
      if (progressData.status === 'complete' && progressData.completed > 0) {
        refreshTurnArtUrls();
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [isOpen, bookId, turnArtStatus, refreshTurnArtUrls]);

  // ============================================================================
  // Preview Loading (NON-BLOCKING)
  // ============================================================================

  const loadPreview = useCallback(async () => {
    try {
      setError(null);
      setStatus('loading');
      
      // Check current Turn Art status
      const progressData = await getTurnArtProgressFromDb(bookId);
      setTurnArtStatus(progressData.status);
      setProgress({ completed: progressData.completed, total: progressData.total });
      
      // Trigger background generation if pending (only when Turn Art is enabled)
      if (FEATURES.TURN_ART_GENERATION && (progressData.status === 'pending' || progressData.status === null)) {
        console.log('[PreviewModal] Triggering background Turn Art generation');
        triggerTurnArtGeneration(bookId); // Fire-and-forget
      }
      
      // Load Turn Codes (fast, doesn't block on illustrations)
      const pageTexts = blocks.map(block => linesToPlainText(block.lines));
      const turnCodes = await loadTurnCodes(pageTexts);
      turnCodesRef.current = turnCodes;
      
      // Load color mappings
      const { data: mappings } = await supabase
        .from('turn_color_mappings')
        .select('color_name, hex_code, description');
      if (mappings?.length) {
        setColorMappings(mappings.map(m => ({
          ...m,
          description: m.description ?? undefined,
        })));
      }
      
      // Fetch current Turn Art URLs (may be partial or empty)
      const allSourceIds = blocks.flatMap(b => b.sourcePageIds);
      const { data: pagesWithArt } = await supabase
        .from('scanned_book_pages')
        .select('id, enhanced_image_url')
        .in('id', allSourceIds.length > 0 ? allSourceIds : ['__none__']);
      
      const artByPageId = new Map(
        (pagesWithArt || []).map(p => [p.id, p.enhanced_image_url])
      );
      
      // Build pages for BookReadingView (with placeholders for missing Turn Art)
      const pages: ReadingPage[] = blocks.map((block) => {
        const pageText = linesToPlainText(block.lines);
        const turnArtUrl = block.sourcePageIds[0] 
          ? artByPageId.get(block.sourcePageIds[0]) || null
          : null;
        
        return {
          text: pageText,
          turnArtUrl, // May be null - BookReadingView will show placeholder
          words: extractWordsWithTurnCodes(pageText, turnCodes),
        };
      });
      
      setPreviewPages(pages);
      setStatus('ready');
      
    } catch (err) {
      console.error('[PreviewModal] Load failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load preview');
      setStatus('error');
    }
  }, [bookId, blocks]);

  // ============================================================================
  // Helpers
  // ============================================================================

  const loadTurnCodes = async (pageTexts: string[]): Promise<Map<string, string>> => {
    const allWords = pageTexts.flatMap(text => 
      text.toLowerCase().replace(/[^a-z'\s-]/g, ' ').split(/\s+/).filter(w => w.length > 0)
    );
    const uniqueWords = [...new Set(allWords)];
    if (!uniqueWords.length) return new Map();
    
    const { data } = await supabase
      .from('turn_color_words')
      .select('word, turn_code')
      .in('word', uniqueWords);
    
    return new Map((data || []).map(row => [row.word.toLowerCase(), row.turn_code]));
  };

  const extractWordsWithTurnCodes = (
    text: string, 
    turnCodes: Map<string, string>
  ): ReadingPage['words'] => {
    // Use split(/\s+/) to preserve punctuation attached to words
    // TurnColorSentence expects words like "Hello," not just "Hello"
    // It extracts punctuation via extractWordParts() for proper rendering
    const tokens = text.split(/\s+/).filter(t => t.length > 0);
    return tokens.map(word => {
      const cleanWord = word.toLowerCase().replace(/[^a-z'-]/g, '');
      const turnCode = turnCodes.get(cleanWord) || null;
      return { word, turnCode, inLexicon: !!turnCode };
    });
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await onPublish();
    } finally {
      setPublishing(false);
    }
  };

  // Handler to refresh preview after Turn Art changes (must be before early return - rules of hooks)
  const handleArtUpdated = useCallback(() => {
    setArtRefreshKey(k => k + 1);
    refreshTurnArtUrls();
  }, [refreshTurnArtUrls]);

  // Check if Turn Art is still generating
  const isGeneratingTurnArt = turnArtStatus === 'pending' || turnArtStatus === 'generating';

  // Handler for Turn Code validation errors - opens fix modal
  const handleTurnCodeError = useCallback((error: TurnCodeError) => {
    // Dedupe: only report each word once per session
    const errorKey = `${error.word}:${error.turnCode}`;
    if (reportedErrorsRef.current.has(errorKey)) return;
    reportedErrorsRef.current.add(errorKey);
    
    // Log to error tracker for monitoring
    errorTracker.dataIntegrity(
      `Invalid turn_code for "${error.word}": ${error.error}`,
      'turn_color_words',
      { metadata: { word: error.word, turnCode: error.turnCode, extractedLetters: error.extractedLetters } }
    );
    
    // Store for modal
    setTurnCodeError(error);
    setShowTurnCodeFix(true);
  }, []);

  // Handler when Turn Code is fixed
  const handleTurnCodeFixed = useCallback((word: string, newTurnCode: string) => {
    const normalizedWord = word.toLowerCase();
    
    // Update the local turnCodes cache
    turnCodesRef.current.set(normalizedWord, newTurnCode);
    
    // Clear the reported error so it can be re-detected if still wrong
    const errorKey = `${word}:${turnCodeError?.turnCode}`;
    reportedErrorsRef.current.delete(errorKey);
    
    // Update previewPages state to reflect the new turnCode
    // This is the actual fix - we must update the baked-in word data
    setPreviewPages(prev => prev.map(page => ({
      ...page,
      words: page.words.map(w => {
        const cleanWord = w.word.toLowerCase().replace(/[^a-z'-]/g, '');
        if (cleanWord === normalizedWord) {
          return { ...w, turnCode: newTurnCode, inLexicon: true };
        }
        return w;
      }),
    })));
    
    // Increment key to force re-render (belt and suspenders)
    setArtRefreshKey(k => k + 1);
  }, [turnCodeError]);

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header - global dark design */}
      <header className="flex-shrink-0 bg-white/5 backdrop-blur-sm border-b border-white/10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-white/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="font-semibold text-white">{bookTitle}</h2>
              {bookAuthor && <p className="text-sm text-white/50">by {bookAuthor}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Turn Art generation progress badge (only when enabled) */}
            {FEATURES.TURN_ART_GENERATION && isGeneratingTurnArt && progress.total > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5E5CE6]/20 border border-[#5E5CE6]/30">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5E5CE6]" />
                <span className="text-xs font-medium text-[#5E5CE6]">
                  Turn Art: {progress.completed}/{progress.total}
                </span>
              </div>
            )}
            
            {status === 'ready' && activeTab === 'preview' && (
              <span className="text-sm text-white/50">
                {currentPageIndex + 1} / {previewPages.length}
              </span>
            )}
            {status === 'ready' && (
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#30D158] text-white font-medium hover:brightness-110 disabled:opacity-50 transition-colors"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Publish
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/80 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs (Turn Art tab hidden when generation is disabled) */}
        <div className="px-4 flex gap-1">
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'preview'
                ? 'bg-white/10 text-white border-t border-x border-white/10'
                : 'text-white/50 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          {FEATURES.TURN_ART_GENERATION && (
            <button
              onClick={() => setActiveTab('turn-art')}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'turn-art'
                  ? 'bg-white/10 text-white border-t border-x border-white/10'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
            >
              <Palette className="w-4 h-4" />
              Turn Art
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-black">
        {activeTab === 'preview' && (
          <>
            {status === 'loading' && (
              <div className="flex-1 flex items-center justify-center bg-[#0A0A0A]">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-[#0A84FF] animate-spin mx-auto mb-4" />
                  <p className="text-white/50">Loading preview...</p>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="flex-1 flex items-center justify-center bg-[#0A0A0A]">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-[#FF453A] mx-auto mb-4" />
                  <p className="text-white text-lg font-medium mb-2">Load Failed</p>
                  <p className="text-white/50 mb-4">{error}</p>
                  <button
                    onClick={loadPreview}
                    className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/15 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {status === 'ready' && previewPages.length > 0 && (
              <div className="flex-1 bg-[#0A0A0A]">
                <BookReadingView
                  key={artRefreshKey}
                  pages={previewPages}
                  currentPageIndex={currentPageIndex}
                  onPageChange={setCurrentPageIndex}
                  colorMappings={colorMappings}
                  phase={2}
                  fontSize={28}
                  lineSpacing={1.8}
                  showNavigation={true}
                  showProgress={true}
                  enableKeyboard={true}
                  enableTouch={true}
                  height="100%"
                  theme={FEATURES.TURN_ART_GENERATION ? 'dark' : 'light'}
                  mode={FEATURES.TURN_ART_GENERATION ? 'illustrated' : 'text-only'}
                  showTurnArtPlaceholder={FEATURES.TURN_ART_GENERATION && isGeneratingTurnArt}
                  onTurnCodeError={handleTurnCodeError}
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'turn-art' && (
          <div className="flex-1 overflow-auto p-6 bg-[#0A0A0A]">
            <div className="max-w-4xl mx-auto">
              <TurnArtEditor
                bookId={bookId}
                bookTitle={bookTitle}
                blocks={blocks}
                onArtUpdated={handleArtUpdated}
                variant="dark"
              />
            </div>
          </div>
        )}
      </main>

      {/* Turn Code Fix Modal */}
      {turnCodeError && (
        <TurnCodeFixModal
          isOpen={showTurnCodeFix}
          onClose={() => setShowTurnCodeFix(false)}
          error={turnCodeError}
          onFixed={handleTurnCodeFixed}
        />
      )}
    </div>
  );
}

export default PreviewModal;
